import React, { useEffect, useState } from "react";
import {
  Row, Col, Card, Table, Spin, Button, Input, notification, Alert,
  Modal, Select, Tag, Tooltip, Radio, Typography, Divider, Space, Form,
} from "antd";
import axios from "axios";
import { baseUrl } from "../../config";
import {
  PhoneOutlined, SoundOutlined, HistoryOutlined, SyncOutlined,
  PlusCircleOutlined, WalletOutlined, ReloadOutlined, ApiOutlined,
  CheckCircleOutlined, CloseCircleOutlined,
} from "@ant-design/icons";
import { hasPermission } from "../../components/permissions";
import moment from "moment";

const { Option } = Select;
const { Text } = Typography;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("accessToken")}`,
});

// ── Aarvi error code descriptions ─────────────────────────────────────────────
const AARVI_ERRORS = {
  "001": "Parameter Missing",
  "002": "Parameter is blank",
  "003": "Request IP not in whitelist — add your server IP on Aarvi panel",
  "005": "Invalid Credentials — check username/token in API Settings",
  "006": "Account is Inactive",
  "008": "Exceeded 50 contacts per API call",
  "009": "Invalid Announcement ID — check the ID exists on Aarvi panel",
  "010": "Invalid Caller ID — update it in API Settings or clear it to use default",
  "014": "No plan assigned",
  "015": "Voice validity expired — recharge on Aarvi panel",
  "016": "Low voice balance",
  "017": "No gateway available",
  "018": "Announcement file missing on Aarvi panel — re-upload",
  "019": "Invalid voice plan",
  "020": "Caller ID not hosted — leave Caller ID blank to use Aarvi default",
};

function aarviErrorMsg(aarviResp) {
  if (!aarviResp) return null;
  const r = typeof aarviResp === "string" ? (() => { try { return JSON.parse(aarviResp); } catch(_){return null;} })() : aarviResp;
  if (!r || r.status !== "error") return null;
  return `[${r.code}] ${AARVI_ERRORS[r.code] || r.desc || "Unknown error"}`;
}

function CallingModule() {
  // ── Members ──────────────────────────────────────────────────────────────────
  const [members, setMembers]         = useState([]);
  const [pagination, setPagination]   = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit]             = useState(20);
  const [loading, setLoading]         = useState(false);
  const [scope, setScope]             = useState("all_members");
  const [filters, setFilters]         = useState({ name: "", phone: "", village: "", district: "" });

  // ── Announcements ─────────────────────────────────────────────────────────────
  const [announcements, setAnnouncements] = useState([]);  // from local DB
  const [annLoading, setAnnLoading]       = useState(false);
  const [syncing, setSyncing]             = useState(false);
  const [syncMsg, setSyncMsg]             = useState(null);

  // Manual register form
  const [regVisible, setRegVisible]   = useState(false);
  const [regId, setRegId]             = useState("");
  const [regName, setRegName]         = useState("");
  const [regSaving, setRegSaving]     = useState(false);

  // ── Balance ───────────────────────────────────────────────────────────────────
  const [balance, setBalance]       = useState(null);
  const [balLoading, setBalLoading] = useState(false);

  // ── Connection test ───────────────────────────────────────────────────────────
  const [connTesting, setConnTesting] = useState(false);
  const [connResult, setConnResult]   = useState(null);

  // ── Quick Test Call ───────────────────────────────────────────────────────────
  const [testPhone, setTestPhone]     = useState("");
  const [testAnnId, setTestAnnId]     = useState(null);
  const [testLoading, setTestLoading] = useState(false);
  const [testResult, setTestResult]   = useState(null);

  // ── Bulk broadcast ────────────────────────────────────────────────────────────
  const [broadcastVisible, setBroadcastVisible] = useState(false);
  const [bcAnnId, setBcAnnId]           = useState(null);
  const [bcCampaignName, setBcCampaignName] = useState("");
  const [bcCustomNumbers, setBcCustomNumbers] = useState("");
  const [bcScope, setBcScope]           = useState("all_members");
  const [bcFilterCount, setBcFilterCount] = useState(null);
  const [bcLoading, setBcLoading]       = useState(false);

  // ── Individual call ───────────────────────────────────────────────────────────
  const [callVisible, setCallVisible]   = useState(false);
  const [callUser, setCallUser]         = useState(null);
  const [callAnnId, setCallAnnId]       = useState(null);
  const [callLoading, setCallLoading]   = useState(false);

  // ── Campaigns history ─────────────────────────────────────────────────────────
  const [campaignsVisible, setCampaignsVisible] = useState(false);
  const [campaigns, setCampaigns]         = useState([]);
  const [campaignsPag, setCampaignsPag]   = useState({});
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  // ── Report ────────────────────────────────────────────────────────────────────
  const [reportVisible, setReportVisible] = useState(false);
  const [reportData, setReportData]       = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  useEffect(() => {
    fetchMembers(1, limit, scope, filters);
    loadAnnouncementList();
    loadBalance();
  }, []); // eslint-disable-line

  // ── Load local DB announcement list ──────────────────────────────────────────
  const loadAnnouncementList = async () => {
    setAnnLoading(true);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/announcement-list`, { headers: authHeaders() });
      if (resp.data.status) setAnnouncements(resp.data.result || []);
    } catch (_) {}
    finally { setAnnLoading(false); }
  };

  // ── Sync from Aarvi panel ─────────────────────────────────────────────────────
  const handleSync = async () => {
    setSyncing(true); setSyncMsg(null);
    try {
      const resp = await axios.post(`${baseUrl}/voice-call/sync-announcements`, {}, { headers: authHeaders() });
      if (resp.data.status) {
        setAnnouncements(resp.data.result || []);
        setSyncMsg({ type: "success", text: resp.data.message });
      } else {
        setSyncMsg({ type: "warning", text: resp.data.message });
      }
    } catch (e) {
      setSyncMsg({ type: "error", text: e.message });
    } finally { setSyncing(false); }
  };

  // ── Manual register announcement ──────────────────────────────────────────────
  const handleRegisterAnn = async () => {
    if (!regId.trim()) { notification.warning({ message: "Enter Announcement ID", placement: "topRight" }); return; }
    setRegSaving(true);
    try {
      const resp = await axios.post(
        `${baseUrl}/voice-call/announcement-save`,
        { announcement_id: regId.trim(), announcement_name: regName.trim() || `Announcement ${regId.trim()}` },
        { headers: authHeaders() }
      );
      if (resp.data.status) {
        notification.success({ message: "Announcement registered.", placement: "topRight" });
        setRegId(""); setRegName(""); setRegVisible(false);
        loadAnnouncementList();
      } else {
        notification.error({ message: resp.data.message, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally { setRegSaving(false); }
  };

  // ── Balance ───────────────────────────────────────────────────────────────────
  const loadBalance = async () => {
    setBalLoading(true);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/balance`, { headers: authHeaders() });
      if (resp.data.status) setBalance(resp.data.balance);
    } catch (_) {}
    finally { setBalLoading(false); }
  };

  // ── Connection test ───────────────────────────────────────────────────────────
  const testConnection = async () => {
    setConnTesting(true); setConnResult(null);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/test-connection`, { headers: authHeaders() });
      setConnResult(resp.data);
      if (resp.data.status) setBalance(null); // reload balance after successful connect
    } catch (e) {
      setConnResult({ status: false, reason: e.message });
    } finally { setConnTesting(false); }
  };

  // ── Quick Test Call ───────────────────────────────────────────────────────────
  const handleTestCall = async () => {
    const phone = testPhone.replace(/\D/g, "");
    if (phone.length < 10) {
      notification.warning({ message: "Enter a valid 10-digit phone number", placement: "topRight" });
      return;
    }
    if (!testAnnId) {
      notification.warning({ message: "Select an announcement", placement: "topRight" });
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    try {
      const resp = await axios.post(
        `${baseUrl}/voice-call/broadcast-individual`,
        { announcement_id: testAnnId, phone_no: phone, campaign_name: `Test Call: ${phone}` },
        { headers: authHeaders() }
      );
      setTestResult(resp.data);
    } catch (e) {
      setTestResult({ status: false, message: e.message });
    } finally {
      setTestLoading(false);
    }
  };

  // ── Members ───────────────────────────────────────────────────────────────────
  const fetchMembers = async (page, lim, sc, flt) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: lim, scope: sc });
      if (flt.name)     params.append("name",     flt.name);
      if (flt.phone)    params.append("phone",    flt.phone);
      if (flt.village)  params.append("village",  flt.village);
      if (flt.district) params.append("district", flt.district);
      const resp = await axios.get(`${baseUrl}/voice-call/members?${params}`, { headers: authHeaders() });
      if (resp.data.status) {
        setMembers(resp.data.result);
        setPagination({ current: page, pageSize: lim, total: resp.data.totalCount });
        setCurrentPage(page);
        setLimit(lim);
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally { setLoading(false); }
  };

  const handleScopeChange = (sc) => { setScope(sc); fetchMembers(1, limit, sc, filters); };
  const handleFilterChange = (field, value) => {
    const f = { ...filters, [field]: value };
    setFilters(f);
    fetchMembers(1, limit, scope, f);
  };

  // ── Bulk broadcast ────────────────────────────────────────────────────────────
  const fetchBcCount = async (sc) => {
    if (sc === "custom_numbers") { setBcFilterCount(null); return; }
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/filter-count?scope=${sc}`, { headers: authHeaders() });
      if (resp.data.status) setBcFilterCount(resp.data.count);
    } catch (_) {}
  };

  const openBroadcastModal = () => {
    setBcAnnId(null); setBcCampaignName(""); setBcCustomNumbers("");
    setBcScope("all_members"); setBcFilterCount(null);
    setBroadcastVisible(true);
    fetchBcCount("all_members");
  };

  const handleBroadcast = async () => {
    if (!bcAnnId) {
      notification.warning({ message: "Select an announcement", placement: "topRight" }); return;
    }
    if (bcScope === "custom_numbers" && !bcCustomNumbers.trim()) {
      notification.warning({ message: "Enter phone numbers", placement: "topRight" }); return;
    }
    setBcLoading(true);
    try {
      const resp = await axios.post(
        `${baseUrl}/voice-call/broadcast-filter`,
        { announcement_id: bcAnnId, campaign_name: bcCampaignName || "Voice Campaign",
          scope: bcScope, filters: {}, custom_numbers: bcCustomNumbers },
        { headers: authHeaders() }
      );
      if (resp.data.status) {
        notification.success({ message: resp.data.message, placement: "topRight" });
        setBroadcastVisible(false);
      } else {
        const friendlyErr = aarviErrorMsg(resp.data.aarvi_response) || resp.data.message;
        notification.error({ message: friendlyErr, duration: 0, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally { setBcLoading(false); }
  };

  // ── Individual call ───────────────────────────────────────────────────────────
  const openCallModal = (user) => { setCallUser(user); setCallAnnId(null); setCallVisible(true); };

  const handleCall = async () => {
    if (!callAnnId) {
      notification.warning({ message: "Select an announcement", placement: "topRight" }); return;
    }
    setCallLoading(true);
    try {
      const resp = await axios.post(
        `${baseUrl}/voice-call/broadcast-individual`,
        { announcement_id: callAnnId, phone_no: callUser.phone_no,
          user_id: callUser.user_id, campaign_name: `Call: ${callUser.name || callUser.phone_no}` },
        { headers: authHeaders() }
      );
      if (resp.data.status) {
        notification.success({ message: resp.data.message, placement: "topRight" });
        setCallVisible(false);
      } else {
        const friendlyErr = aarviErrorMsg(resp.data.aarvi_response) || resp.data.message;
        notification.error({ message: friendlyErr, duration: 0, placement: "topRight" });
      }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" });
    } finally { setCallLoading(false); }
  };

  // ── Campaigns ─────────────────────────────────────────────────────────────────
  const loadCampaigns = async (page = 1, lim = 10) => {
    setCampaignsLoading(true);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/campaigns?page=${page}&limit=${lim}`, { headers: authHeaders() });
      if (resp.data.status) {
        setCampaigns(resp.data.result);
        setCampaignsPag({ current: page, pageSize: lim, total: resp.data.totalCount });
      }
    } catch (_) {}
    finally { setCampaignsLoading(false); }
  };

  const fetchReport = async (campaignId) => {
    setReportLoading(true); setReportData(null); setReportVisible(true);
    try {
      const resp = await axios.get(`${baseUrl}/voice-call/report/${campaignId}`, { headers: authHeaders() });
      if (resp.data.status) setReportData(resp.data);
      else { notification.error({ message: resp.data.message, placement: "topRight" }); setReportVisible(false); }
    } catch (e) {
      notification.error({ message: e.message, placement: "topRight" }); setReportVisible(false);
    } finally { setReportLoading(false); }
  };

  // ── Announcement Select ───────────────────────────────────────────────────────
  const AnnouncementSelect = ({ value, onChange, placeholder = "Select announcement" }) => (
    <Select
      showSearch
      allowClear
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      style={{ width: "100%" }}
      loading={annLoading}
      optionFilterProp="label"
      notFoundContent={
        <div style={{ textAlign: "center", padding: 8, color: "#888", fontSize: 12 }}>
          No announcements — click "Sync from Aarvi" or "Register by ID"
        </div>
      }
    >
      {announcements.map(a => (
        <Option key={a.announcement_id} value={String(a.announcement_id)} label={`${a.announcement_name || a.announcement_id} (${a.announcement_id})`}>
          <span style={{ fontWeight: 600 }}>{a.announcement_name || `Announcement ${a.announcement_id}`}</span>
          <span style={{ color: "#888", fontSize: 11, marginLeft: 8 }}>ID: {a.announcement_id}</span>
        </Option>
      ))}
    </Select>
  );

  const validBcNums = bcCustomNumbers
    .split(/[\s,;\n]+/)
    .filter(n => n.replace(/\D/g, "").length >= 10).length;

  const memberColumns = [
    {
      title: "S.No", key: "sno", width: 55,
      render: (_, __, idx) => <span style={{ fontWeight: 600, fontSize: 12 }}>{(limit * (currentPage - 1)) + idx + 1}</span>,
    },
    {
      title: "Name", dataIndex: "name", key: "name", width: 190,
      render: t => <span style={{ fontWeight: 600, fontSize: 13 }}>{t || "—"}</span>,
    },
    {
      title: "Phone", dataIndex: "phone_no", key: "phone_no", width: 140,
      render: t => <span style={{ fontFamily: "monospace", fontSize: 13 }}>{t || "—"}</span>,
    },
    {
      title: "Village", dataIndex: "per_village", key: "per_village", width: 130,
      render: t => <span style={{ fontSize: 12 }}>{t || "—"}</span>,
    },
    {
      title: "District", dataIndex: "per_district", key: "per_district", width: 130,
      render: t => <span style={{ fontSize: 12 }}>{t || "—"}</span>,
    },
    {
      title: "Last Payment", dataIndex: "last_payment_date", key: "last_payment_date", width: 130,
      render: t => t
        ? <span style={{ fontSize: 12 }}>{moment(t).format("DD MMM YYYY")}</span>
        : <Tag color="red" style={{ fontSize: 11 }}>Never</Tag>,
    },
    {
      title: "Call", key: "action", width: 70, fixed: "right",
      render: (_, record) => (
        <Tooltip title={`Call ${record.name || record.phone_no}`}>
          <Button
            size="small"
            icon={<PhoneOutlined />}
            style={{ color: "#722ed1", borderColor: "#722ed1" }}
            onClick={() => openCallModal(record)}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      {/* ── Balance + top bar ─────────────────────────────────────────────────── */}
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col>
          <Card size="small" style={{ borderRadius: 8, minWidth: 220 }}>
            <Space>
              <WalletOutlined style={{ color: "#722ed1", fontSize: 20 }} />
              <div>
                <div style={{ fontSize: 11, color: "#888" }}>Voice Credits (Aarvi)</div>
                {balLoading ? <Spin size="small" /> : (
                  <div style={{ fontWeight: 700, fontSize: 16 }}>
                    {balance?.VOC_CRD ?? "—"}
                    <Button type="link" size="small" icon={<ReloadOutlined />} onClick={loadBalance} style={{ padding: "0 4px" }} />
                  </div>
                )}
              </div>
            </Space>
          </Card>
        </Col>
        <Col>
          <Card size="small" style={{ borderRadius: 8 }}>
            <Space direction="vertical" size={4}>
              <Button
                icon={<ApiOutlined />}
                loading={connTesting}
                onClick={testConnection}
                size="small"
              >
                Test Aarvi Connection
              </Button>
              {connResult && (
                <div style={{ fontSize: 11 }}>
                  {connResult.status ? (
                    <span style={{ color: "#52c41a" }}>
                      <CheckCircleOutlined /> Connected — IP: {connResult.server_ip}
                    </span>
                  ) : (
                    <div>
                      <div style={{ color: "#ff4d4f" }}>
                        <CloseCircleOutlined /> {connResult.reason}
                      </div>
                      {connResult.fix && (
                        <div style={{ color: "#fa8c16", marginTop: 2, maxWidth: 340 }}>
                          ⚠️ {connResult.fix}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Space>
          </Card>
        </Col>
      </Row>

      {/* ── Quick Test Call ───────────────────────────────────────────────────── */}
      <Card
        bordered={false}
        style={{ marginBottom: 16, borderRadius: 10, borderLeft: "4px solid #722ed1" }}
        bodyStyle={{ padding: "16px 20px" }}
      >
        <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4, color: "#722ed1" }}>
          <PhoneOutlined style={{ marginRight: 8 }} />
          Quick Test Call
        </div>
        <div style={{ fontSize: 12, color: "#888", marginBottom: 14 }}>
          Enter your own phone number + an announcement to test if calls reach you before broadcasting to members.
        </div>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} sm={8}>
            <Input
              placeholder="Your phone number (e.g. 9876543210)"
              value={testPhone}
              onChange={e => { setTestPhone(e.target.value); setTestResult(null); }}
              prefix={<span style={{ color: "#888", fontSize: 12 }}>+91</span>}
              maxLength={13}
            />
          </Col>
          <Col xs={24} sm={10}>
            <AnnouncementSelect
              value={testAnnId}
              onChange={v => { setTestAnnId(v); setTestResult(null); }}
              placeholder="Select announcement to play"
            />
          </Col>
          <Col xs={24} sm={6}>
            <Button
              type="primary"
              icon={<PhoneOutlined />}
              loading={testLoading}
              onClick={handleTestCall}
              style={{ backgroundColor: "#722ed1", borderColor: "#722ed1", width: "100%" }}
              disabled={!testPhone.trim() || !testAnnId}
            >
              Call My Number
            </Button>
          </Col>
        </Row>

        {testResult && (
          <div style={{ marginTop: 12 }}>
            {testResult.status ? (
              <Alert
                type="success"
                showIcon
                message={
                  <span>
                    <strong>Call submitted to Aarvi!</strong>
                    {testResult.aarvi_response?.data?.[0]?.unique_id && (
                      <span style={{ marginLeft: 8, fontSize: 12, color: "#555" }}>
                        Unique ID: <code>{testResult.aarvi_response.data[0].unique_id}</code>
                      </span>
                    )}
                  </span>
                }
                description="Your phone should ring within 30–60 seconds. If it doesn't, check the IP whitelist on Aarvi panel."
              />
            ) : (
              <Alert
                type="error"
                showIcon
                message={aarviErrorMsg(testResult.aarvi_response) || testResult.message}
                description={
                  testResult.message?.includes("timeout") || testResult.message?.includes("timed out")
                    ? "Your server IP is not whitelisted on Aarvi panel. Log into Aarvi → Settings → IP Whitelist → add your IP."
                    : undefined
                }
              />
            )}
          </div>
        )}
      </Card>

      {/* ── Announcement Management ────────────────────────────────────────────── */}
      <Card
        bordered={false}
        style={{ marginBottom: 16, borderRadius: 10 }}
        title={
          <Space>
            <SoundOutlined style={{ color: "#fff" }} />
            <span style={{ color: "#fff", fontWeight: 700 }}>Announcements</span>
          </Space>
        }
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', borderRadius: '10px 10px 0 0'}}
        extra={
          <Space>
            <Button
              icon={<SyncOutlined spin={syncing} />}
              onClick={handleSync}
              loading={syncing}
              size="small"
              style={{ color: "#fff", borderColor: "#fff" }}
            >
              Sync from Aarvi
            </Button>
            <Button
              icon={<PlusCircleOutlined />}
              onClick={() => setRegVisible(true)}
              size="small"
              style={{ color: "#fff", borderColor: "#fff" }}
            >
              Register by ID
            </Button>
          </Space>
        }
      >
        {syncMsg && (
          <Alert
            type={syncMsg.type}
            message={syncMsg.text}
            showIcon
            closable
            style={{ marginBottom: 12 }}
            onClose={() => setSyncMsg(null)}
          />
        )}
        <div style={{ fontSize: 12, color: "#888", marginBottom: 12 }}>
          Announcements uploaded via Aarvi panel won't appear automatically. Click <strong>Sync from Aarvi</strong> to pull them.
          If sync doesn't return them, use <strong>Register by ID</strong> to add the ID manually from Aarvi's panel.
        </div>

        <Table
          size="small"
          rowKey="announcement_id"
          dataSource={announcements}
          loading={annLoading}
          pagination={false}
          columns={[
            {
              title: "ID", dataIndex: "announcement_id", key: "id", width: 100,
              render: t => <Tag color="purple">{t}</Tag>,
            },
            {
              title: "Name / File", dataIndex: "announcement_name", key: "name",
              render: t => <span style={{ fontWeight: 600 }}>{t || "—"}</span>,
            },
            {
              title: "URL", dataIndex: "announcement_url", key: "url",
              render: t => t
                ? <a href={t} target="_blank" rel="noopener noreferrer" style={{ fontSize: 11 }}>Listen</a>
                : <span style={{ color: "#bbb", fontSize: 11 }}>—</span>,
            },
            {
              title: "Added", dataIndex: "created_at", key: "created_at", width: 130,
              render: t => <span style={{ fontSize: 11 }}>{t ? moment(t).format("DD-MM-YYYY HH:mm") : "—"}</span>,
            },
          ]}
        />
      </Card>

      {/* ── Member Calling List ────────────────────────────────────────────────── */}
      <Card
        bordered={false}
        style={{ borderRadius: 10 }}
        title={<span style={{ color: "#fff", fontWeight: 700 }}>Member Calling List</span>}
        headStyle={{ background: '#f8fafc', borderBottom: '1.5px solid #e2e8f0', borderRadius: '10px 10px 0 0'}}
      >
        <div style={{ marginBottom: 12 }}>
          <Radio.Group value={scope} onChange={e => handleScopeChange(e.target.value)}>
            <Radio.Button value="all_members">All Active Members</Radio.Button>
            <Radio.Button value="calling_list">Overdue Only (25+ days)</Radio.Button>
          </Radio.Group>
        </div>

        <Row gutter={[8, 8]} style={{ marginBottom: 12 }}>
          {["name", "phone", "village", "district"].map(field => (
            <Col xs={6} key={field}>
              <Input
                placeholder={field.charAt(0).toUpperCase() + field.slice(1)}
                value={filters[field]}
                onChange={e => handleFilterChange(field, e.target.value)}
                allowClear
              />
            </Col>
          ))}
        </Row>

        <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
          {hasPermission("/calling-module", "editPermission") && (
            <Button
              type="primary"
              icon={<SoundOutlined />}
              onClick={openBroadcastModal}
              style={{ backgroundColor: "#722ed1", borderColor: "#722ed1" }}
            >
              Bulk Voice Call
            </Button>
          )}
          <Button
            icon={<HistoryOutlined />}
            onClick={() => { setCampaignsVisible(true); loadCampaigns(); }}
          >
            Campaigns History
          </Button>
          <Select value={limit} onChange={v => fetchMembers(1, v, scope, filters)} style={{ width: 110 }}>
            {[20, 40, 60, 100].map(v => <Option key={v} value={v}>{v} / page</Option>)}
          </Select>
        </div>

        <Table
          loading={loading}
          size="small"
          rowKey="user_id"
          dataSource={members}
          columns={memberColumns}
          scroll={{ x: "max-content" }}
          pagination={{
            ...pagination,
            showTotal: total => `Total ${total} members`,
            onChange: (page, pageSize) => fetchMembers(page, pageSize, scope, filters),
          }}
        />
      </Card>

      {/* ── Register Announcement Modal ────────────────────────────────────────── */}
      <Modal
        title={<><PlusCircleOutlined style={{ marginRight: 8 }} />Register Aarvi Announcement</>}
        open={regVisible}
        onCancel={() => { setRegVisible(false); setRegId(""); setRegName(""); }}
        onOk={handleRegisterAnn}
        okText="Register"
        okButtonProps={{ loading: regSaving }}
        width={420}
      >
        <Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 16 }}>
          Copy the announcement ID from Aarvi panel and register it here so it appears in the dropdown.
        </Text>
        <Form layout="vertical">
          <Form.Item label="Announcement ID (from Aarvi panel)" required>
            <Input
              placeholder="e.g. 700400"
              value={regId}
              onChange={e => setRegId(e.target.value)}
            />
          </Form.Item>
          <Form.Item label="Name / Label (optional)">
            <Input
              placeholder="e.g. Payment Reminder Hindi"
              value={regName}
              onChange={e => setRegName(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Bulk Broadcast Modal ───────────────────────────────────────────────── */}
      <Modal
        title={<><SoundOutlined style={{ color: "#722ed1", marginRight: 8 }} />Bulk Voice Broadcast</>}
        open={broadcastVisible}
        onCancel={() => setBroadcastVisible(false)}
        onOk={handleBroadcast}
        okText="Start Broadcast"
        okButtonProps={{ loading: bcLoading, style: { backgroundColor: "#722ed1", borderColor: "#722ed1" } }}
        width={560}
      >
        <Form layout="vertical">
          <Form.Item label="Who to Call">
            <Radio.Group value={bcScope} onChange={e => { setBcScope(e.target.value); fetchBcCount(e.target.value); }}>
              <Radio.Button value="all_members">All Members</Radio.Button>
              <Radio.Button value="calling_list">Overdue Only</Radio.Button>
              <Radio.Button value="custom_numbers">Custom Numbers</Radio.Button>
            </Radio.Group>
            <div style={{ fontSize: 11, color: "#888", marginTop: 4 }}>
              {bcScope === "all_members"    && "All active members in the database."}
              {bcScope === "calling_list"   && "Members whose last payment was more than 25 days ago."}
              {bcScope === "custom_numbers" && "Enter specific phone numbers below."}
            </div>
          </Form.Item>

          {bcScope !== "custom_numbers" && bcFilterCount !== null && (
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f0e6ff", borderRadius: 6, border: "1px solid #d3adf7" }}>
              <PhoneOutlined style={{ marginRight: 6, color: "#722ed1" }} />
              <strong>{bcFilterCount}</strong> members will receive this call.
            </div>
          )}

          {bcScope === "custom_numbers" && (
            <Form.Item label="Phone Numbers *">
              <Input.TextArea
                rows={4}
                placeholder={"One per line or comma-separated:\n9876543210\n9876543211, 9876543212"}
                value={bcCustomNumbers}
                onChange={e => setBcCustomNumbers(e.target.value)}
              />
              {bcCustomNumbers.trim() && (
                <div style={{ fontSize: 12, color: "#722ed1", marginTop: 4 }}>
                  {validBcNums} valid 10-digit number{validBcNums !== 1 ? "s" : ""} entered
                </div>
              )}
            </Form.Item>
          )}

          <Form.Item label="Campaign Name">
            <Input placeholder="e.g. Payment Reminder June 2026" value={bcCampaignName} onChange={e => setBcCampaignName(e.target.value)} />
          </Form.Item>

          <Form.Item label="Announcement *">
            <AnnouncementSelect value={bcAnnId} onChange={setBcAnnId} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Individual Call Modal ──────────────────────────────────────────────── */}
      <Modal
        title={<><PhoneOutlined style={{ color: "#722ed1", marginRight: 8 }} />Call — {callUser?.name || callUser?.phone_no}</>}
        open={callVisible}
        onCancel={() => setCallVisible(false)}
        onOk={handleCall}
        okText="Call Now"
        okButtonProps={{ loading: callLoading, style: { backgroundColor: "#722ed1", borderColor: "#722ed1" } }}
        width={440}
      >
        {callUser && (
          <div style={{ marginBottom: 14, padding: "10px 12px", background: "#fafafa", borderRadius: 6, border: "1px solid #e8e8e8" }}>
            <div><strong>Name:</strong> {callUser.name || "—"}</div>
            <div><strong>Phone:</strong> <span style={{ fontFamily: "monospace" }}>{callUser.phone_no}</span></div>
            {callUser.per_village && <div><strong>Village:</strong> {callUser.per_village}</div>}
          </div>
        )}
        <Form layout="vertical">
          <Form.Item label="Select Announcement *">
            <AnnouncementSelect value={callAnnId} onChange={setCallAnnId} />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Campaigns History ──────────────────────────────────────────────────── */}
      <Modal
        title={<><HistoryOutlined style={{ marginRight: 8 }} />Voice Call Campaigns</>}
        open={campaignsVisible}
        onCancel={() => setCampaignsVisible(false)}
        footer={null}
        width={960}
      >
        <Table
          loading={campaignsLoading}
          size="small"
          rowKey="id"
          dataSource={campaigns}
          pagination={{
            ...campaignsPag,
            onChange: (p, ps) => loadCampaigns(p, ps),
            showTotal: t => `Total ${t} campaigns`,
          }}
          columns={[
            { title: "ID", dataIndex: "id", key: "id", width: 55 },
            { title: "Campaign Name", dataIndex: "campaign_name", key: "campaign_name", width: 200 },
            {
              title: "Type", dataIndex: "call_type", key: "call_type", width: 80,
              render: t => <Tag color={t === "bulk" ? "purple" : "blue"}>{t}</Tag>,
            },
            { title: "Contacts", dataIndex: "contact_count", key: "contact_count", width: 80 },
            {
              title: "Aarvi Status", key: "aarvi_status", width: 160,
              render: (_, r) => {
                const err = aarviErrorMsg(r.aarvi_response);
                if (err) return <Tag color="red" style={{ fontSize: 11, whiteSpace: "normal" }}>{err}</Tag>;
                if (r.aarvi_unique_id) return <Tag color="green">Submitted</Tag>;
                return <Tag color="default">—</Tag>;
              },
            },
            {
              title: "Date", dataIndex: "created_at", key: "created_at", width: 140,
              render: t => moment(t).format("DD-MM-YYYY HH:mm"),
            },
            {
              title: "Report", key: "action", width: 70,
              render: (_, r) => <Button size="small" onClick={() => fetchReport(r.id)}>View</Button>,
            },
          ]}
        />
      </Modal>

      {/* ── Call Report Modal ──────────────────────────────────────────────────── */}
      <Modal
        title="Voice Call Report"
        open={reportVisible}
        onCancel={() => setReportVisible(false)}
        footer={null}
        width={700}
      >
        {reportLoading ? (
          <div style={{ textAlign: "center", padding: 40 }}><Spin /></div>
        ) : reportData ? (
          <div>
            <div style={{ marginBottom: 12, padding: "8px 12px", background: "#f0f2f5", borderRadius: 6 }}>
              <strong>{reportData.campaign?.campaign_name}</strong>
              <span style={{ marginLeft: 16, color: "#888" }}>Contacts: {reportData.campaign?.contact_count}</span>
              <span style={{ marginLeft: 16, color: "#888" }}>{moment(reportData.campaign?.created_at).format("DD-MM-YYYY HH:mm")}</span>
            </div>
            {reportData.campaign?.aarvi_unique_id && (
              <div style={{ fontSize: 12, color: "#666", marginBottom: 8 }}>
                Aarvi Unique ID: <code>{reportData.campaign.aarvi_unique_id}</code>
              </div>
            )}
            {reportData.note && (
              <Alert type="info" message={reportData.note} style={{ marginBottom: 8 }} />
            )}
            <pre style={{ background: "#fafafa", padding: 12, borderRadius: 6, fontSize: 12, maxHeight: 360, overflow: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
              {JSON.stringify(reportData.report, null, 2)}
            </pre>
          </div>
        ) : null}
      </Modal>
    </div>
  );
}

export default CallingModule;
